import { AreaCategory, UserProfile } from 'fitbeat.gym-core';
import { TimeSlotEligibility, TimeSlotType, WorkoutType } from 'fitbeat.models';
import React, { FunctionComponent } from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { connect } from 'react-redux';
import { TranslateProp } from '../../common-types';
import { IAppStore } from '../../HomeWorkouts';
import { GymResolver } from '../../services';
import Theme from '../../theme';
import { defaultScale } from '../../utils/scaling';
import { getAreaDisplayName, getAreaIcon } from '../../utils/areaCategoryMapping';
import {
  setPendingSlot as setPendingSlotAction,
} from '../actions';
import { ZoneTimeSlots } from '../components';
import { timeslotDurationsSelector } from '../selectors';

interface IParentProps {
  t: TranslateProp;
  userProfile: UserProfile;
  areaCategory: AreaCategory;
  preferredStartTime: number;
  setTimeSlotEligibility(eligibility: TimeSlotEligibility): void;
  setTimeSlotType(timeSlotType: TimeSlotType): void;
}

interface IMapStateProps {
  currentSelectedGymName: string | null;
  pendingSlot?: any;
  isLoadingBookingSlots: boolean;
  timeslotDurations: WorkoutType[];
}

interface IMapDispatchProps {
  setPendingSlot(pendingSlot: any, areaCategory: AreaCategory | undefined): void | undefined;
}

const StudioZoneTimeslotCard: FunctionComponent<IMapStateProps & IMapDispatchProps & IParentProps> = (
  {
    isLoadingBookingSlots,
    userProfile,
    pendingSlot,
    setPendingSlot,
    timeslotDurations,
    setTimeSlotType,
    setTimeSlotEligibility,
    currentSelectedGymName,
    t,
    areaCategory,
    preferredStartTime,
  },
) => {
  if (isLoadingBookingSlots) {
    return (
      <View style={styles.container}>
        <ActivityIndicator
          size='small'
          color={Theme.PRIMARY}
          animating={isLoadingBookingSlots}
          style={styles.loadingContainer}
        />
      </View>
    );
  }

  if (!currentSelectedGymName) {
    return <></>;
  }

  const gym = GymResolver.getByName(currentSelectedGymName);

  const getClassTitle = (workoutType: WorkoutType) => {
    switch (workoutType) {
      case WorkoutType.QuickStaticFunctionalWorkout:
        return t('14Min');
      case WorkoutType.MediumStudioWorkout:
        return t('28Min');
      case WorkoutType.FullStudioWorkout:
        return t('46Min');
      case WorkoutType.QuickHomeOrStaticCardioWorkout:
        return t('10Min');
      case WorkoutType.MediumHomeOrStaticCardioWorkout:
        return t('20Min');
      case WorkoutType.FullHomeOrStaticCardioWorkout:
        return t('30Min');
      case WorkoutType.ExtraLongHomeOrStaticCardioWorkout:
        return t('40Min');
      case WorkoutType.FullStaticCardioWorkout:
        return t('50Min');
      default:
        return undefined;
    }
  };

  return (
    <View style={styles.container}>
      {timeslotDurations.map((duration) => {
        const classTitle = getClassTitle(duration);

        if (!classTitle) {
          return <></>;
        }

        return (
          <ZoneTimeSlots
            key={`${areaCategory}_${duration}`}
            t={t}
            showHeader={false}
            userProfile={userProfile}
            gymTimezone={gym.timezone}
            pendingSlot={pendingSlot}
            setPendingSlot={setPendingSlot}
            setTimeSlotType={setTimeSlotType}
            isLoadingTimeSlots={isLoadingBookingSlots}
            classTitle={classTitle}
            classDuration={duration}
            setTimeSlotEligibility={setTimeSlotEligibility}
            preferredStartTime={preferredStartTime}
            areaCategory={areaCategory}
          />
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    justifyContent: 'flex-end',
    backgroundColor: Theme.WHITE,
    paddingBottom: defaultScale(15),
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    marginTop: defaultScale(20),
  },
});

const mapStateToProps = (state: IAppStore, ownProps: IParentProps): IMapStateProps => ({
  currentSelectedGymName: state.bookings.bookingWizard.currentSelectedGymName,
  isLoadingBookingSlots: state.bookings.bookingWizard.isLoadingBookingSlots,
  timeslotDurations: timeslotDurationsSelector(state, ownProps.areaCategory),
});

const mapDispatchToProps = (dispatch): IMapDispatchProps => ({
  setPendingSlot: (pendingSlot, areaCategory) => dispatch(setPendingSlotAction(pendingSlot, areaCategory)),
});

export default connect(mapStateToProps, mapDispatchToProps)(StudioZoneTimeslotCard);
